import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingSteps, ONBOARDING_TOTAL_STEPS } from 'constants/onboarding.steps';
import { updateOnboardingStatus, fetchConfigurationData } from 'redux/merchant';
import useRedux from 'redux/useRedux';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OnboardingState {
  // Current step index (0-based); -1 = tour not started / idle
  currentStep: number;
  isActive: boolean;
  isCompleted: boolean;
  showWelcome: boolean;
  showCompletion: boolean;
}

interface OnboardingContextValue extends OnboardingState {
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  dismissWelcome: () => void;
  restartTour: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used inside OnboardingProvider');
  return ctx;
};

/** Safe version — returns null when used outside OnboardingProvider (e.g. Sidebar before provider mounts) */
export const useOnboardingSafe = (): OnboardingContextValue | null => {
  return useContext(OnboardingContext);
};

// ─── Provider ────────────────────────────────────────────────────────────────

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { storeState: { Auth }, thunkDispatch } = useRedux();
  const navigate = useNavigate();

  const merchant = Auth?.data?.data?.merchant;
  const token = Auth?.data?.data?.token;
  const merchantId = merchant?.id;

  // ── State ──────────────────────────────────────────────────────────────────
  const [state, setState] = useState<OnboardingState>({
    currentStep: 0,
    isActive: false,
    isCompleted: false,
    showWelcome: false,
    showCompletion: false,
  });

  // Prevent multiple navigations in flight
  const navigatingRef = useRef(false);

  // ── Load onboarding status from backend ────────────────────────────────────
  useEffect(() => {
    if (!merchantId || !token) return;

    thunkDispatch(fetchConfigurationData({ idBusiness: merchantId, token }))
      .unwrap()
      .then((res: any) => {
        if (!res?.data) return;
        const { isOnboardingCompleted, onboardingStep } = res.data;

        if (isOnboardingCompleted === true) {
          setState((prev) => ({ ...prev, isCompleted: true, showWelcome: false }));
        } else {
          // Show welcome modal for merchants who haven't started or are mid-tour
          const resumeStep = typeof onboardingStep === 'number' ? onboardingStep : 0;
          setState((prev) => ({
            ...prev,
            currentStep: resumeStep,
            showWelcome: true,
            isCompleted: false,
          }));
        }
      })
      .catch(() => {
        // If fetch fails, still show the welcome so first-time logic is handled
        setState((prev) => ({ ...prev, showWelcome: true }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, token]);

  // ── Navigate when active step changes ──────────────────────────────────────
  useEffect(() => {
    if (!state.isActive) return;

    const step = onboardingSteps[state.currentStep];
    if (step?.route && !navigatingRef.current) {
      navigatingRef.current = true;
      navigate(step.route);
      // Small delay to allow sidebar to render before we try highlighting
      setTimeout(() => { navigatingRef.current = false; }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep, state.isActive]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const persist = useCallback(
    (step: number, completed: boolean) => {
      if (!merchantId || !token) return;
      thunkDispatch(
        updateOnboardingStatus({
          idBusiness: merchantId,
          token,
          onboardingStep: step,
          isOnboardingCompleted: completed,
        }),
      );
    },
    [merchantId, token, thunkDispatch],
  );

  // ── Actions ────────────────────────────────────────────────────────────────

  const dismissWelcome = useCallback(() => {
    setState((prev) => ({ ...prev, showWelcome: false, isCompleted: true }));
    persist(state.currentStep, true);
  }, [persist, state.currentStep]);

  const startTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showWelcome: false,
      isActive: true,
      currentStep: prev.currentStep, // resume from where they were
    }));
    persist(state.currentStep, false);
  }, [persist, state.currentStep]);

  const nextStep = useCallback(() => {
    setState((prev) => {
      const nextIdx = prev.currentStep + 1;

      if (nextIdx >= ONBOARDING_TOTAL_STEPS) {
        // Tour complete — end silently, no completion modal
        persist(ONBOARDING_TOTAL_STEPS - 1, true);
        return {
          ...prev,
          isActive: false,
          isCompleted: true,
          showCompletion: false,
          currentStep: ONBOARDING_TOTAL_STEPS - 1,
        };
      }

      persist(nextIdx, false);
      return { ...prev, currentStep: nextIdx };
    });
  }, [persist]);

  const prevStep = useCallback(() => {
    setState((prev) => {
      const prevIdx = Math.max(0, prev.currentStep - 1);
      persist(prevIdx, false);
      return { ...prev, currentStep: prevIdx };
    });
  }, [persist]);

  const skipTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: false,
      showWelcome: false,
      isCompleted: true,
    }));
    persist(state.currentStep, true);
  }, [persist, state.currentStep]);

  const completeTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: false,
      isCompleted: true,
      showCompletion: false,
    }));
  }, []);

  const restartTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: 0,
      isActive: true,
      isCompleted: false,
      showCompletion: false,
      showWelcome: false,
    }));
    persist(0, false);
  }, [persist]);

  // ── Context value ──────────────────────────────────────────────────────────
  const value: OnboardingContextValue = {
    ...state,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    dismissWelcome,
    restartTour,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}
